import { BaseSelectorOption } from '@/types';
import styled from '@emotion/styled';
import { ListItem, Tooltip, Typography } from '@mui/material';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import React, { Component } from 'react';
import { KLAYO_COLORS } from '../../themes';
import IntegrationAbbreviation from '../../views/settings/IntegrationAbbreviation';

interface MultiSelectorContentProps<TOption extends BaseSelectorOption = BaseSelectorOption> {
  option: TOption;
  description?: string | null;
  inputValue?: string;
  getItemId?: (option: TOption) => string | number | TOption;
  getItemLabel?: (option: TOption) => string;
  selectorContainerSize?: DOMRect | null;
  props?: any;
}

interface MultiSelectorContentState {
  isOptionTooltipVisible: boolean;
}

export class MultiSelectorContent<
  TOption extends BaseSelectorOption = BaseSelectorOption,
> extends Component<MultiSelectorContentProps<TOption>, MultiSelectorContentState> {
  hoveredOptionRef: any;

  constructor(props: MultiSelectorContentProps<TOption>) {
    super(props);

    this.state = {
      isOptionTooltipVisible: false,
    };

    this.hoveredOptionRef = React.createRef();
  }

  handleOptionMouseEnter = (optionElement: any) => {
    const { selectorContainerSize } = this.props;

    // Get the size of the option content
    const optionSize = optionElement?.current?.getBoundingClientRect();

    // Check if the option is too large
    const isOptionTooLarge = optionSize?.width > (selectorContainerSize?.width || 0);

    if (isOptionTooLarge) {
      // Set the state to show the tooltip
      this.setState({ isOptionTooltipVisible: true });
    }
  };

  handleOptionMouseLeave = () => {
    // Hide the tooltip when the mouse leaves the option
    this.setState({ isOptionTooltipVisible: false });
  };

  render() {
    const { option, description, inputValue, props, getItemId } = this.props;
    const { getItemLabel } = this.props;
    const { isOptionTooltipVisible } = this.state;

    const name = getItemLabel?.(option) || '';
    const matches = match(name, inputValue || '', {
      insideWords: true,
      requireMatchAll: false,
    });
    const parts = parse(name, matches);

    const renderTitle =
      parts.length === 1 ? (
        <>
          {name}{' '}
          <IntegrationAbbreviation
            data={option ? option : null}
            type={IntegrationAbbreviation.dataType.integrationUser.value}
          />
        </>
      ) : (
        parts.map((part, index) => (
          <span
            key={Math.random().toString(36).substring(7)}
            className={part.highlight === true ? ' klayo-selector_highlight' : ''}
          >
            {part.text}
            {index === parts.length - 1 && (
              <IntegrationAbbreviation
                data={option ? option : null}
                type={IntegrationAbbreviation.dataType.integrationUser.value}
              />
            )}
          </span>
        ))
      );

    const renderContent = (
      <>
        {renderTitle}
        {description && (
          <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
            {description}
          </Typography>
        )}
      </>
    );

    return (
      <Tooltip
        PopperProps={{
          style: { maxWidth: 'none' },
        }}
        title={renderContent}
        placement='top'
        open={isOptionTooltipVisible}
        arrow
      >
        <ListItemStyled
          {...props}
          className={`${props?.className ?? ''}${
            description ? ' klayo-multiselector_option--withdescription' : ''
          }`}
          key={String(getItemId?.(option))}
          onMouseEnter={(e) => this.handleOptionMouseEnter(this.hoveredOptionRef)}
          onMouseLeave={this.handleOptionMouseLeave.bind(this)}
        >
          {' '}
          <p ref={this.hoveredOptionRef}>{renderContent}</p>{' '}
        </ListItemStyled>
      </Tooltip>
    );
  }
}

const ListItemStyled = styled(ListItem)`
  :hover {
    .klayo_integration-abbreviation {
      color: ${KLAYO_COLORS.WhiteWH900};
    }
  }

  /* Global app.css locks autocomplete options to height/line-height 40px with hidden overflow,
     which clips the description line — relax it only for two-line options. The doubled class
     out-specifies the global ".MuiAutocomplete-popper li" rule. */
  &&.klayo-multiselector_option--withdescription {
    height: auto;
    line-height: 24px;
    padding-top: 8px;
    padding-bottom: 8px;

    p {
      margin: 0;
      line-height: 24px;
    }
  }
`;
